import { BaseModelType } from "../BaseModel";
import { ModelValue, VariablePrototype } from "../bridge";
import { Option } from "../Option";
import { Prompt } from "../Prompt";
import { VariableRelationship } from "./VariableRelationship";
import { VariableValue } from "./VariableValue";

export enum ModelVariableType {
    Independent = "INDEPENDENT",
    "Selection Data" = "SELECTION_DATA", // sim only
    Iterative = "ITERATIVE", // sim only
    Aggregate = "AGGREGATE",
    Algebraic = "ALGEBRAIC",
    Conditional = "CONDITIONAL",
    Logical = "LOGICAL",
    Comparison = "COMPARISON",
    "Time Shift" = "TIME_SHIFT",
    // "Dot Product Value" = "DOT_PRODUCT_VALUE",
    // "Dot Product Score" = "DOT_PRODUCT_SCORE",
    // "Numerical Comparison Switch" = "NUMERICAL_COMPARISON_SWITCH",
    // "Order Proximity Score" = "ORDER_PROXIMITY_SCORE",
    // "Order Weighted Sum" = "ORDER_WEIGHTED_SUM",
    // "Numerical Range Categorization" = "NUMERICAL_RANGE_CATEGORIZATION",
}
// fraction/ratio/percent?

export enum ModelVariableDataType {
    Number = "NUMBER",
    Boolean = "BOOLEAN",
    Json = "JSON", // structured response: MC, MS, ranking (stored as json_value in model_values)
}

export enum QuestionType {
    MULTIPLE_CHOICE = "MULTIPLE_CHOICE",
    MULTIPLE_SELECT = "MULTIPLE_SELECT",
    NUMERICAL_SLIDER = "NUMERICAL_SLIDER",
    RANK_ORDER = "RANK_ORDER",
    TOGGLE_SWITCH = "TOGGLE_SWITCH",
}

// MODULE_FORM contexts only: question_type → data_type
// MULTIPLE_CHOICE / MULTIPLE_SELECT / RANK_ORDER → JSON
// TOGGLE_SWITCH → BOOLEAN
// NUMERICAL_SLIDER → NUMBER
// Mirrors QuestionType::expectedDataType(); questionTypes.test.ts holds the two together.
export function dataTypeForQuestionType(
    qt: QuestionType,
): ModelVariableDataType {
    switch (qt) {
        case QuestionType.TOGGLE_SWITCH:
            return ModelVariableDataType.Boolean;
        case QuestionType.NUMERICAL_SLIDER:
            return ModelVariableDataType.Number;
        default:
            return ModelVariableDataType.Json;
    }
}

/**
 * The question types whose answers are option-key pointers.
 *
 * **This was called `JSON_QUESTION_TYPES`, in this codebase twice** — once here-ish in
 * `VariableForm.tsx` and once in the PHP request trait. The name stated the *data type* and meant
 * the *option set*. They coincide today and stop coinciding at the first options-less JSON type, so
 * the question to ask a variable is whether its type carries options, never what column it stores in.
 *
 * Second statement of a fact `QuestionType::carriesOptions()` owns; `questionTypes.test.ts` reads
 * the PHP and fails when they part. It cannot be generated — `typescript:transform` emits enum
 * cases, and a capability expressed as a method has no generated counterpart.
 */
export const QUESTION_TYPES_WITH_OPTIONS: ReadonlySet<QuestionType> = new Set([
    QuestionType.MULTIPLE_CHOICE,
    QuestionType.MULTIPLE_SELECT,
    QuestionType.RANK_ORDER,
]);

/** The types that may name a `metadata.question_group_key` — `QuestionType::mayBeGrouped()`. */
export const QUESTION_TYPES_GROUPABLE: ReadonlySet<QuestionType> = new Set([
    QuestionType.NUMERICAL_SLIDER,
    QuestionType.MULTIPLE_SELECT,
]);

export enum ModelVariableScope {
    User = "USER",
    Team = "TEAM",
    Cohort = "COHORT",
    Simulation = "SIMULATION", // unused
}

export enum ModelVariableUnit {
    Percent = "PERCENT",
    Currency = "CURRENCY",
    Custom = "CUSTOM",
    None = "NONE",
}

export enum CurrencyType {
    USD = "USD",
    INR = "INR",
}

// SUM, PRODUCT?
export enum ModelVariableAggregateOperation {
    Average = "AVERAGE",
    Minimum = "MINIMUM",
    Maximum = "MAXIMUM",
    "Standard Deviation" = "STANDARD_DEVIATION",
    Count = "COUNT",
    "Count If" = "COUNT_IF",
    "Cumulative Sum" = "CUMULATIVE_SUM",
    "Cumulative Product" = "CUMULATIVE_PRODUCT",
}

// tool only
export enum ModelVariableResampleFunction {
    sum = "sum", // target default (boolean: count of True)
    mean = "mean", // source default (boolean: proportion of True to False)
    last = "last",
    first = "first",
    mode = "mode",
    all = "all", // boolean (logical AND)
    any = "any", // boolean (logical OR)
    min = "min", // numeric
    max = "max", // numeric
}

type VariableMetadataOption = {
    key: string;
    label: string;
    weight?: number;
    description?: string;
};

export type ToolVariablePreset = {
    key: string;
    label: string;
    value: number;
};

type ModelVariableMetadata = {
    // FORM display metadata
    question_type?: QuestionType;
    options?: VariableMetadataOption[];
    question_group_key?: string;
    // TOOL display primitives: hidden = opt-in hide (element-less only);
    // static = non-interactive readout vs. interactive input.
    hidden?: boolean;
    static?: boolean;
    // TOOL presets: labeled numeric snap targets for an interactive input. Presence drives
    // the control shape (slider+buttons if min/max, else discrete buttons) and, for element-linked
    // levers, overrides benchmark tier presets.
    presets?: ToolVariablePreset[];
};

export type ModelVariable = BaseModelType & {
    variable_prototype_id?: string; // FK: variable_prototypes (nullable)
    metadata?: ModelVariableMetadata;

    // general props
    model_block_id: string;
    variable_key?: string;
    label: string;
    weight: number;
    data_type: ModelVariableDataType;
    unit?: ModelVariableUnit;
    description?: string; // tool specific right now
    // calc
    variable_type: ModelVariableType;
    aggregate_operation?: ModelVariableAggregateOperation;
    is_absolute_value: boolean;
    // display
    is_integer: boolean;
    // default
    default_numerical_value: number;
    default_boolean_value: boolean;

    // flags with various roles (not ideal: different behavior across layers)
    expose_to_facilitator: boolean; // discovery & data studio (show); interactive (highlight)
    expose_to_designer: boolean; // interactive (input); only makes sense for non-target variables
    is_key_metric: boolean; // interactive (output)

    // sim specific
    uses_time: boolean;
    scope?: ModelVariableScope;
    prompt_id?: string; // to do (context refactor): mv
    option_id?: string; // to do (context refactor): mv

    // tool specific
    minimum?: number;
    maximum?: number;
    range_step?: number;
    resample_function?: string;

    // related objects
    variableValues?: VariableValue[];
    baseVariableValues?: VariableValue[];
    sourceVariables?: ModelVariable[];
    sourceVariableRelationships?: VariableRelationship[];
    targetVariables?: ModelVariable[];
    targetVariableRelationships?: VariableRelationship[];
    descendantVariables?: ModelVariable[];
    prompt?: Prompt; // to do (context refactor): mv
    option?: Option; // to do (context refactor): mv
    relationship_count?: number;
    variablePrototype?: VariablePrototype;

    // potential additions:
    // ideal_behavior, ideal_condition, config

    baseModelValues?: ModelValue[];
};

export type VariableDepthObject = {
    depth: number;
    label: string;
    variable_id: string;
};

//
// Model variables can be...

// 1. Behaved through metadata (participation rules)
// --- Describes a variable’s intrinsic or context-layer behavior

// 2. Connected through joins (local collaborations)
// --- Defines how a variable interacts with other domain entities

// 3. Linked conceptually through prototypes (canonical identity)

// example metadata structure:
// type VariableMetadata = BaseModelType & {
//     model_variable_id: string;
//     kind: string; // enum('workspace','interactive','simulation','file','integration') // 'general'?
//     settings: any; // jsonb, flexible key-value schema
// };

// example connection/join tables:
// model_variable_option
// model_variable_widget
// model_variable_tool_template
// model_variable_variable_group
// model_variable_value_generator

// type ModelVariableOption = {
//     id: string;
//     model_variable_id: string;
//     option_id: string;
//     prompt_id: string; // ?
// }

// type OptionVariableConnection // table option_variable_connections
